import { DestroyRef, Injectable, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CARD_EVENT_SOURCE } from '../api/card.transport';
import { CardStore } from './card.store';
import type { CardEvent } from './card.ws';

const DEDUP_CAP = 200;

@Injectable({ providedIn: 'root' })
export class CardEventsService {
  private readonly source = inject(CARD_EVENT_SOURCE);
  private readonly store = inject(CardStore);
  private readonly destroyRef = inject(DestroyRef);

  private readonly seen = new Set<string>();

  readonly threeDs = signal<{ cardId: string; type: string; code: string | null } | null>(null);

  readonly stale = signal(false);

  /**
   * Bumped on every `card.transaction` event. A counter rather than a flag so
   * consumers reload on *each* event — a boolean would latch on the first one
   * and never change again.
   */
  readonly transactionsRevision = signal(0);

  constructor() {
    this.store.registerResetHook(() => {
      this.seen.clear();
      this.threeDs.set(null);
      this.stale.set(false);
      this.transactionsRevision.set(0);
    });

    this.source
      .events()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(event => this.apply(event));
  }

  clearThreeDs(): void {
    this.threeDs.set(null);
  }

  consumeStale(): boolean {
    const value = this.stale();
    this.stale.set(false);
    return value;
  }

  private apply(event: CardEvent): void {
    if (this.isDuplicate(event)) return;

    switch (event.name) {
      case 'card.operation.progress': {
        this.touchOperation(event.payload.operationId);
        this.store.patchOperationStatus(event.payload.operationId, event.payload.status);
        return;
      }
      case 'card.operation.failed': {
        this.store.patchOperationFailure(event.payload.operationId, event.payload.failureReason);
        this.stale.set(true);
        return;
      }
      case 'card.transaction': {
        this.transactionsRevision.update(revision => revision + 1);
        return;
      }
      case 'card.operation.refunded': {
        this.touchOperation(event.payload.operationId);
        this.store.patchOperationStatus(event.payload.operationId, 'REFUNDED');
        this.store.setRefund(
          event.payload.operationId,
          event.payload.amountRaw,
          event.payload.txHash,
        );
        return;
      }
      case 'card.issued': {
        this.stale.set(true);
        return;
      }
      case 'card.topup.completed': {
        this.store.patchOperationStatus(event.payload.operationId, 'COMPLETED');
        this.stale.set(true);
        return;
      }
      case 'card.status.changed': {
        this.stale.set(true);
        return;
      }
      case 'card.3ds.otp': {
        this.threeDs.set(event.payload);
        return;
      }
      default:
        return;
    }
  }

  private touchOperation(operationId: string): void {
    if (!this.store.operations().some(op => op.id === operationId)) this.stale.set(true);
  }

  private isDuplicate(event: CardEvent): boolean {
    const eventId = 'eventId' in event.payload ? event.payload.eventId : null;
    if (!eventId) return false;
    const key = `${event.name}:${eventId}`;
    if (this.seen.has(key)) return true;
    this.seen.add(key);
    if (this.seen.size > DEDUP_CAP) {
      const oldest = this.seen.values().next().value;
      if (oldest !== undefined) this.seen.delete(oldest);
    }
    return false;
  }
}
